import { WarrantSubscriptionPeriod } from '@finance-lab/utils/types'

/**
 * Find the subscription period closest to a specific date for a warrant.
 * - returns null if no subscription periods are found
 * - if we have a current period, based on subStartDate and subEndDate, return it
 * - if we have no current period, but we have future periods, return the nearest one to current date
 * - if we have no current or future periods, return the last period
 */
export function findSubscriptionPeriodCloseToDate(
    { subscriptionPeriods }: { subscriptionPeriods?: WarrantSubscriptionPeriod[] } = {},
    dateISOString: string,
) {
    if (!subscriptionPeriods) return null
    // if we have a current period, based on subStartDate and subEndDate, return it
    // if we have no current period, but we have future periods, return the nearest one to current date
    // if we have no current or future periods, return the last period
    const futurePeriods: WarrantSubscriptionPeriod[] = []
    let currentPeriod: WarrantSubscriptionPeriod | null = null
    let lastPeriod: WarrantSubscriptionPeriod | null = null
    subscriptionPeriods.forEach((period) => {
        if (period.subStartDate === null || period.subEndDate === null) {
            futurePeriods.push(period)
            return
        }

        const subStartDate = new Date(period.subStartDate).toISOString()
        const subEndDate = new Date(period.subEndDate).toISOString()

        if (dateISOString >= subStartDate && dateISOString <= subEndDate) {
            currentPeriod = period
        } else if (dateISOString < subStartDate) {
            futurePeriods.push(period)
        } else {
            lastPeriod = period
        }
    })
    if (currentPeriod) return currentPeriod
    if (futurePeriods.length > 0) {
        futurePeriods.sort(
            (a, b) => new Date(a.subStartDate as string).getTime() - new Date(b.subStartDate as string).getTime(),
        )
        return futurePeriods[0]
    }
    return lastPeriod
}

export const getAmountForPriceAtDate = (
    price: string | number | null,
    totalNumberOfWarrants: number | null,
    subscriptionPeriods: WarrantSubscriptionPeriod[],
    dateISOString: string,
) => {
    if (price === null || isNaN(+price)) return null
    if (totalNumberOfWarrants === null) return null

    const currentPeriod: WarrantSubscriptionPeriod | null = findSubscriptionPeriodCloseToDate(
        { subscriptionPeriods },
        dateISOString,
    )

    if (
        !currentPeriod?.subStartDate ||
        currentPeriod.warrantsRequired === null ||
        currentPeriod.sharesReceived === null
    )
        return null

    const previousPeriods = subscriptionPeriods.filter(
        (period) =>
            period.subStartDate && new Date(period.subStartDate) < new Date(currentPeriod.subStartDate as string),
    )
    const previouslyExercisedWarrants = previousPeriods.reduce(
        (acc, { sharesSubscribed, sharesReceived, warrantsRequired }) => {
            if (sharesSubscribed === null || sharesReceived === null || warrantsRequired === null) return acc

            return acc + (sharesSubscribed * warrantsRequired) / sharesReceived
        },
        0,
    )

    const remainingWarrants = totalNumberOfWarrants - previouslyExercisedWarrants

    return ((+price * remainingWarrants) / currentPeriod.warrantsRequired) * currentPeriod.sharesReceived
}

export const hasSubPeriodPrelPrice = (subPeriod: WarrantSubscriptionPeriod) =>
    subPeriod.warrantSubPrice === null && subPeriod.prelSubPrice !== null
